import { storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';

/**
 * File Service
 * Handles file uploads, downloads, and management for course materials
 */
export const fileService = {
  /**
   * Upload a file to Firebase Storage
   * @param {File} file - The file to upload
   * @param {string} path - The storage path (e.g., 'course_files/courseId/fileName')
   * @param {Function} onProgress - Progress callback (optional)
   * @returns {Object} { url, error }
   */
  async uploadFile(file, path, onProgress) {
    try {
      const storageRef = ref(storage, path);
      
      // Upload file
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return { 
        url: downloadURL,
        path: snapshot.ref.fullPath,
        name: file.name,
        size: file.size,
        type: file.type,
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      return { error: error.message };
    }
  },

  /**
   * Delete a file from Firebase Storage
   * @param {string} path - The storage path
   * @returns {Object} { success, error }
   */
  async deleteFile(path) {
    try {
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting file:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get download URL for a file
   * @param {string} path - The storage path
   * @returns {Object} { url, error }
   */
  async getDownloadURL(path) {
    try {
      const storageRef = ref(storage, path);
      const url = await getDownloadURL(storageRef);
      return { url };
    } catch (error) {
      console.error('Error getting download URL:', error);
      return { error: error.message };
    }
  },

  /**
   * List all files in a directory
   * @param {string} path - The directory path
   * @returns {Object} { files, error }
   */
  async listFiles(path) {
    try {
      const storageRef = ref(storage, path);
      const result = await listAll(storageRef);
      
      const files = await Promise.all(
        result.items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef);
          return {
            name: itemRef.name,
            fullPath: itemRef.fullPath,
            url,
          };
        })
      );

      return { files };
    } catch (error) {
      console.error('Error listing files:', error);
      return { files: [], error: error.message };
    }
  },

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  },

  /**
   * Get file extension from filename
   * @param {string} filename - The filename
   * @returns {string} File extension
   */
  getFileExtension(filename) {
    return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
  },

  /**
   * Get file icon based on extension
   * @param {string} filename - The filename
   * @returns {string} Icon name for MaterialCommunityIcons
   */
  getFileIcon(filename) {
    const ext = this.getFileExtension(filename).toLowerCase();
    
    const iconMap = {
      // Documents
      pdf: 'file-pdf-box',
      doc: 'file-word-box',
      docx: 'file-word-box',
      txt: 'file-document-outline',
      
      // Spreadsheets
      xls: 'file-excel-box',
      xlsx: 'file-excel-box',
      csv: 'file-delimited-outline',
      
      // Presentations
      ppt: 'file-powerpoint-box',
      pptx: 'file-powerpoint-box',
      
      // Images
      jpg: 'file-image-outline',
      jpeg: 'file-image-outline',
      png: 'file-image-outline',
      gif: 'file-image-outline',
      svg: 'file-image-outline',
      
      // Archives
      zip: 'folder-zip-outline',
      rar: 'folder-zip-outline',
      '7z': 'folder-zip-outline',
      
      // Code
      js: 'language-javascript',
      py: 'language-python',
      java: 'language-java',
      html: 'language-html5',
      css: 'language-css3',
      
      // Video
      mp4: 'file-video-outline',
      mov: 'file-video-outline',
      avi: 'file-video-outline',
      
      // Audio
      mp3: 'file-music-outline',
      wav: 'file-music-outline',
    };

    return iconMap[ext] || 'file-outline';
  },

  /**
   * Validate file before upload
   * @param {File} file - The file to validate
   * @param {Object} options - Validation options { maxSize, allowedTypes }
   * @returns {Object} { valid, error }
   */
  validateFile(file, options = {}) {
    const { maxSize = 100 * 1024 * 1024, allowedTypes = [] } = options; // Default 100MB

    // Check file size
    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File size exceeds maximum allowed size of ${this.formatFileSize(maxSize)}`,
      };
    }

    // Check file type if specified
    if (allowedTypes.length > 0) {
      const ext = this.getFileExtension(file.name).toLowerCase();
      if (!allowedTypes.includes(ext)) {
        return {
          valid: false,
          error: `File type .${ext} is not allowed. Allowed types: ${allowedTypes.join(', ')}`,
        };
      }
    }

    return { valid: true };
  },
};

